import { useRoute, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import AdminLayout from "@/components/layout-admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft, CheckCircle2, Clock, DollarSign, TrendingUp,
  Phone, Mail, User, Sparkles, AlertCircle, CalendarDays,
  Building2, Wrench,
} from "lucide-react";

type CleanerJob = {
  id: number;
  apartmentId: number;
  apartmentName: string | null;
  status: string;
  scheduledDate: string;
  completedAt: string | null;
  jobType: string;
  cleanerFee: string | null;
};

type CleanerFinancial = {
  id: number;
  description: string | null;
  type: string | null;
  cleanerAmount: string | null;
  status: string;
  createdAt: string;
};

type CleanerProfileData = {
  user: {
    id: number;
    fullName: string | null;
    username: string;
    email: string | null;
    phone: string | null;
    role: string;
  };
  jobs: CleanerJob[];
  financials: CleanerFinancial[];
  summary: {
    totalJobs: number;
    completed: number;
    pending: number;
    inProgress: number;
    totalEarnings: number;
    pendingEarnings: number;
    jobsThisMonth: number;
    completionRate: number;
  };
};

const fmt = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 });

const statusColors: Record<string, string> = {
  completed: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
  in_progress: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  cancelled: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
};

export default function AdminCleanerProfile() {
  const [, params] = useRoute("/admin/cleaner/:userId");
  const userId = params?.userId ? Number(params.userId) : null;

  const { data, isLoading } = useQuery<CleanerProfileData>({
    queryKey: ["/api/admin/cleaner", userId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/admin/cleaner/${userId}`);
      return res.json();
    },
    enabled: !!userId,
  });

  const user = data?.user;
  const summary = data?.summary;

  const roleLabel = user?.role === "maintenance" ? "Maintenance Tech" : "Cleaner";
  const roleIcon = user?.role === "maintenance"
    ? <Wrench className="w-4 h-4 text-blue-500" />
    : <Sparkles className="w-4 h-4 text-primary" />;

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Link href="/admin/users">
            <button className="p-2 rounded-lg hover:bg-muted transition-colors" data-testid="btn-back-users">
              <ArrowLeft className="w-4 h-4" />
            </button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold font-display">
              {isLoading ? "Loading…" : (user?.fullName || user?.username || "Profile")}
            </h1>
            <p className="text-sm text-muted-foreground">{roleLabel} Profile</p>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-28 rounded-xl" />
            ))}
          </div>
        ) : !data ? (
          <div className="text-center py-16 text-muted-foreground">User not found</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="md:col-span-1">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <User className="w-4 h-4" /> Contact Info
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="font-semibold text-lg">{user?.fullName || user?.username}</p>
                  {user?.email && (
                    <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="text-cleaner-email">
                      <Mail className="w-3.5 h-3.5" /> {user.email}
                    </p>
                  )}
                  {user?.phone && (
                    <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="text-cleaner-phone">
                      <Phone className="w-3.5 h-3.5" /> {user.phone}
                    </p>
                  )}
                  <Badge variant="outline" className="text-xs capitalize flex items-center gap-1 w-fit">
                    {roleIcon} {roleLabel}
                  </Badge>
                </CardContent>
              </Card>

              <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard
                  icon={<CheckCircle2 className="w-4 h-4 text-green-500" />}
                  label="Completed"
                  value={String(summary?.completed ?? 0)}
                  sub="jobs finished"
                />
                <StatCard
                  icon={<CalendarDays className="w-4 h-4 text-blue-500" />}
                  label="This Month"
                  value={String(summary?.jobsThisMonth ?? 0)}
                  sub="jobs scheduled"
                />
                <StatCard
                  icon={<DollarSign className="w-4 h-4 text-primary" />}
                  label="Total Earned"
                  value={fmt(summary?.totalEarnings ?? 0)}
                  sub="all time"
                />
                <StatCard
                  icon={<TrendingUp className="w-4 h-4 text-amber-500" />}
                  label="Completion"
                  value={`${summary?.completionRate ?? 0}%`}
                  sub="success rate"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard
                icon={<Clock className="w-4 h-4 text-amber-500" />}
                label="Pending Jobs"
                value={String(summary?.pending ?? 0)}
                sub="awaiting completion"
              />
              <StatCard
                icon={<Sparkles className="w-4 h-4 text-blue-500" />}
                label="In Progress"
                value={String(summary?.inProgress ?? 0)}
                sub="currently active"
              />
              <StatCard
                icon={<AlertCircle className="w-4 h-4 text-amber-600" />}
                label="Pending Pay"
                value={fmt(summary?.pendingEarnings ?? 0)}
                sub="awaiting payment"
              />
            </div>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm font-medium">
                  <Building2 className="w-4 h-4" /> Recent Jobs
                  <Badge variant="secondary">{data.jobs.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {data.jobs.length === 0 ? (
                  <div className="py-12 text-center text-sm text-muted-foreground">No jobs found.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/40">
                          <th className="text-left font-medium text-muted-foreground px-4 py-2">Apartment</th>
                          <th className="text-left font-medium text-muted-foreground px-4 py-2 hidden sm:table-cell">Type</th>
                          <th className="text-left font-medium text-muted-foreground px-4 py-2">Status</th>
                          <th className="text-left font-medium text-muted-foreground px-4 py-2 hidden md:table-cell">Scheduled</th>
                          <th className="text-right font-medium text-muted-foreground px-4 py-2 hidden lg:table-cell">Pay</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {data.jobs.slice(0, 100).map(job => (
                          <tr key={job.id} className="hover:bg-muted/30 transition-colors" data-testid={`row-job-${job.id}`}>
                            <td className="px-4 py-3">
                              <p className="font-medium">{job.apartmentName || `Apt #${job.apartmentId}`}</p>
                            </td>
                            <td className="px-4 py-3 hidden sm:table-cell">
                              <Badge variant="outline" className="text-xs capitalize">
                                {(job.jobType || "").replace(/_/g, " ")}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[job.status] || "bg-muted text-muted-foreground"}`}>
                                {job.status === "completed" && <CheckCircle2 className="w-3 h-3" />}
                                {job.status === "pending" && <Clock className="w-3 h-3" />}
                                <span className="capitalize">{(job.status || "").replace(/_/g, " ")}</span>
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-muted-foreground hidden md:table-cell">
                              {job.scheduledDate ? new Date(job.scheduledDate).toLocaleDateString() : "—"}
                            </td>
                            <td className="px-4 py-3 text-right hidden lg:table-cell">
                              {job.cleanerFee ? (
                                <span className="font-medium tabular-nums">{fmt(parseFloat(job.cleanerFee))}</span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {data.jobs.length > 100 && (
                      <p className="text-xs text-muted-foreground text-center py-3">
                        Showing 100 of {data.jobs.length} jobs
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Financial Records
                  <Badge variant="secondary">{data.financials.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {data.financials.length === 0 ? (
                  <div className="py-10 text-center text-sm text-muted-foreground">No financial records yet.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/40">
                          <th className="text-left font-medium text-muted-foreground px-4 py-2">Description</th>
                          <th className="text-left font-medium text-muted-foreground px-4 py-2 hidden sm:table-cell">Type</th>
                          <th className="text-right font-medium text-muted-foreground px-4 py-2">Amount</th>
                          <th className="text-left font-medium text-muted-foreground px-4 py-2 hidden md:table-cell">Status</th>
                          <th className="text-left font-medium text-muted-foreground px-4 py-2 hidden lg:table-cell">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {data.financials.slice(0, 100).map(f => (
                          <tr key={f.id} className="hover:bg-muted/30 transition-colors" data-testid={`row-financial-${f.id}`}>
                            <td className="px-4 py-2.5">
                              <p className="font-medium truncate max-w-[200px]">{f.description || "—"}</p>
                            </td>
                            <td className="px-4 py-2.5 hidden sm:table-cell">
                              <Badge variant="outline" className="text-xs capitalize">
                                {(f.type || "").replace(/_/g, " ")}
                              </Badge>
                            </td>
                            <td className="px-4 py-2.5 text-right font-medium tabular-nums">
                              {fmt(parseFloat(f.cleanerAmount || "0"))}
                            </td>
                            <td className="px-4 py-2.5 hidden md:table-cell">
                              <Badge
                                variant="secondary"
                                className={`text-xs ${f.status === "paid"
                                  ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                                  : "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300"}`}
                              >
                                {f.status === "paid"
                                  ? <CheckCircle2 className="w-3 h-3 mr-1 inline" />
                                  : <Clock className="w-3 h-3 mr-1 inline" />}
                                {f.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-muted-foreground hidden lg:table-cell">
                              {f.createdAt ? new Date(f.createdAt).toLocaleDateString() : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {data.financials.length > 100 && (
                      <p className="text-xs text-muted-foreground text-center py-3">
                        Showing 100 of {data.financials.length} records
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function StatCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub: string }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center gap-2 mb-1">
          {icon}
          <span className="text-xs text-muted-foreground font-medium">{label}</span>
        </div>
        <p className="text-xl font-bold tabular-nums">{value}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
      </CardContent>
    </Card>
  );
}
